<script lang="ts">
	/**
	 * The purchase terms this sale was agreed to, and when (§4.7).
	 *
	 * The acceptances were always recorded — that is what `clickwrap_acceptances` is for, and the
	 * words are copied onto the row precisely so they can be quoted back — but nothing rendered
	 * them, so the evidence existed and was unreadable by the only people who ever need it. The
	 * question support is asked is "did they agree to X before buying this", and it is asked about
	 * one purchase: a customer-wide list would answer it for somebody who has bought once and for
	 * nobody else.
	 *
	 * **The body shown is the snapshot on the acceptance, never the live term.** A clickwrap is
	 * editable, so rendering today's wording under a date from last March would be evidence of
	 * something nobody agreed to — and would look exactly like evidence of something they did. The
	 * version badge says whether the two still match, which is a comparison rather than a reason to
	 * substitute one for the other.
	 *
	 * Each term is collapsed by default. These are legal texts and there are usually several: open
	 * they would push the rest of the screen — invoices, refunds, entitlements — below the fold on
	 * every visit, for something read on maybe one visit in twenty.
	 */

	import { Badge } from 'flowbite-svelte';
	import EmptyState from './EmptyState.svelte';
	import Panel from './Panel.svelte';
	import {
		ACCEPTANCE_VERSION_COPY,
		acceptanceAnswer,
		acceptanceVersionNote,
		formatFriendlyDate
	} from '../Utilities';
	import type { ClickwrapAcceptance } from '../AppTypes';

	/**
	 * Undefined and empty are different answers and the copy says so.
	 *
	 * Undefined is a server that predates the field, where "nothing was accepted" would be a claim
	 * this screen cannot support; empty is a purchase that genuinely required no terms.
	 */
	export let acceptances: ClickwrapAcceptance[] | undefined = undefined;

	/** Which one is open. One at a time: two legal texts side by side is neither of them read. */
	let openUUID: string = '';

	/*
	 * The question, the answer and the "has this moved since" note all come from `Utilities`.
	 *
	 * Two screens render an acceptance — this one, per purchase, and the customer-wide register on
	 * the support page — and they are laid out differently on purpose. What they may not differ on
	 * is what an acceptance *says*, so those rules live in one place and this reads them.
	 */

	function fnToggle(uuid: string): void {
		openUUID = openUUID === uuid ? '' : uuid;
	}
</script>

<Panel title="Terms accepted" faIcon="file-signature">
	<svelte:fragment slot="titleRight">
		{#if acceptances !== undefined}
			<span class="text-sm text-default">{acceptances.length}</span>
		{/if}
	</svelte:fragment>

	{#if acceptances === undefined}
		<EmptyState
			compact
			title="This server does not report accepted terms."
			description="Nothing was recorded either way on this screen, so check the purchase terms directly."
		/>
	{:else if acceptances.length === 0}
		<EmptyState
			compact
			title="No terms applied to this purchase."
			description="Terms are set per product and per product family on the catalogue screen; a purchase made before one was attached carries none."
		/>
	{:else}
		<ul class="space-y-2">
			{#each acceptances as acceptance (acceptance.uuid)}
				<li class="widget bg-depth">
					<!--
						A real button, so the keyboard reaches it and the whole row is the target.
						`aria-expanded` because what it discloses is elsewhere on the page.
					-->
					<button
						type="button"
						class="flex w-full flex-wrap items-center gap-x-3 gap-y-1 text-left"
						aria-expanded={openUUID === acceptance.uuid}
						on:click={() => fnToggle(acceptance.uuid)}
					>
						<i
							class="fa-solid {openUUID === acceptance.uuid ? 'fa-angle-up' : 'fa-angle-down'} text-default"
						/>
						<span class="font-medium">{acceptance.title}</span>
						<Badge color="dark">v{acceptance.version}</Badge>
						{#if acceptanceVersionNote(acceptance) !== null}
							<!--
								Not a fault, and worded so it does not read as one: the customer agreed
								to what they were shown, and the term has been edited or withdrawn
								since. It is here because support otherwise compares this text against
								the catalogue screen and finds a difference with no explanation.
							-->
							<Badge color="yellow">
								{ACCEPTANCE_VERSION_COPY[acceptanceVersionNote(acceptance) ?? 'edited']}
							</Badge>
						{/if}
						{#if acceptanceAnswer(acceptance) !== null}
							<!--
								The term's question and its answer, on the header line rather than
								under it.

								It sits **outside the collapse** because it is the one part of an
								acceptance somebody comes back to look up — which project a licence
								was granted for, which institution accepted it — and burying it
								inside a legal text nobody expands makes the record unreadable for
								its commonest use. On the header line specifically because it
								identifies *which* acceptance this row is: two purchases of the same
								term differ by nothing else, and a title plus a date is not enough
								to tell them apart. The row already wraps (`flex-wrap`), so a long
								answer drops to its own line rather than pushing the date off the
								end.
							-->
							<span class="text-sm text-default">{acceptanceAnswer(acceptance)}</span>
						{/if}
						<span class="grow" />
						<span class="text-sm text-default">
							Accepted {formatFriendlyDate(acceptance.acceptedAt)}
						</span>
					</button>

					{#if openUUID === acceptance.uuid}
						<!--
							`richtext`, because Tailwind's preflight strips the paragraph and list
							styling out of any HTML the app did not author — and a term of service
							rendered as one unbroken block is unreadable exactly where it matters.
						-->
						<div class="richtext mt-3 border-t border-widget-color pt-3 text-sm">
							{@html acceptance.bodyHTML}
						</div>
					{/if}
				</li>
			{/each}
		</ul>
	{/if}
</Panel>
